import { PlusIcon } from '@heroicons/react/24/outline';
import { HeartIcon } from '@heroicons/react/24/solid';
import { Button, Chip, Input, Spinner } from '@heroui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import { type FC, useEffect, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useFetchAccountQuery } from '@/slices/account-api-slice.ts';
import { useFetchAccountRepresentativesForAccountQuery } from '@/slices/account-representative-api-slice.ts';
import { useSecurityContext } from '../hooks/security-provider.tsx';
import type { AccountCategoryModel } from '../types/account-category.type.ts';
import type { AccountRepresentativeListModel } from '../types/account-representative.type.ts';
import { RepresentativeStatusEnum } from '../types/representative-status.type.ts';

interface RepresentativeAccessProps {
  existingCategories?: AccountCategoryModel[];
  existingAccountRepresentatives?: AccountRepresentativeListModel[];
  onChange: (
    accountRepresentatives: AccountRepresentativeListModel[],
    accountCategories: AccountCategoryModel[],
  ) => void;
}

const RepresentativeAccess: FC<RepresentativeAccessProps> = ({
  existingAccountRepresentatives,
  existingCategories,
  onChange,
}) => {
  const { currentAccount } = useSecurityContext();
  const { data } = useFetchAccountQuery(currentAccount?.key ?? skipToken);
  const { approved: accountReps, isLoading } = useFetchAccountRepresentativesForAccountQuery(
    currentAccount?.key ?? skipToken,
    {
      selectFromResult: ({ data, isLoading }) => ({
        approved: data?.filter(
          x => x.representativeStatus?.id === RepresentativeStatusEnum.Approved,
        ),
        isLoading: isLoading,
      }),
    },
  );
  const { watch, register, control } = useForm({
    values: {
      filter: '',
      accountCategories: existingCategories ?? [],
      accountRepresentatives: existingAccountRepresentatives ?? [],
    },
  });
  const {
    fields: categoryFields,
    append: appendCategory,
    remove: removeCategory,
  } = useFieldArray({
    control: control,
    name: 'accountCategories',
  });

  const {
    fields: repFields,
    append: appendRep,
    remove: removeRep,
  } = useFieldArray({
    control: control,
    name: 'accountRepresentatives',
  });

  const filterWatch = watch('filter');
  const categoriesWatch = watch('accountCategories');
  const repsWatch = watch('accountRepresentatives');

  useEffect(() => {
    onChange(repsWatch, categoriesWatch);
  }, [categoriesWatch, repsWatch]);

  // Extract filtering logic into descriptive variables
  const matchesRepSearchFilter = (
    rep: AccountRepresentativeListModel,
    searchTerm: string,
  ): boolean => {
    if (!searchTerm) {
      return true;
    }
    const lowerSearch = searchTerm.toLowerCase();
    return (
      rep.representativeFirstName.toLowerCase().includes(lowerSearch) ||
      rep.representativeLastName.toLowerCase().includes(lowerSearch) ||
      rep.representativeTitle?.toLowerCase().includes(lowerSearch) ||
      `${rep.representativeFirstName} ${rep.representativeLastName}`
        .toLowerCase()
        .includes(lowerSearch)
    );
  };

  const matchesCategorySearchFilter = (
    category: AccountCategoryModel,
    searchTerm: string,
  ): boolean => {
    if (!searchTerm) {
      return true;
    }
    return category.description.toLowerCase().includes(searchTerm.toLowerCase());
  };

  // Available categories (not already selected, matching filter)
  const availableCategories = useMemo(() => {
    const allCategories = data?.categories ?? [];
    const selectedCategoryIds = categoriesWatch.map(c => c.id);

    return allCategories
      .filter(cat => !selectedCategoryIds.includes(cat.id))
      .filter(cat => matchesCategorySearchFilter(cat, filterWatch));
  }, [data?.categories, categoriesWatch, filterWatch]);

  // Available reps (not already selected, not in selected category, matching filter)
  const availableReps = useMemo(() => {
    const allReps = accountReps ?? [];
    const selectedRepIds = repsWatch.map(r => r.id);
    const selectedCategoryIds = categoriesWatch.map(c => c.id);

    return allReps
      .filter(rep => !selectedRepIds.includes(rep.id))
      .filter(rep => !selectedCategoryIds.includes(rep.accountCategory?.id))
      .filter(rep => matchesRepSearchFilter(rep, filterWatch));
  }, [accountReps, repsWatch, categoriesWatch, filterWatch]);

  // UI state variables
  const hasAvailableCategories = availableCategories.length > 0;
  const hasAvailableReps = availableReps.length > 0;
  const showCategoriesEmptyState = !data?.categories?.length;
  const showRepsEmptyState = !isLoading && !accountReps?.length;

  return (
    <div className='flex flex-col'>
      <p className='text-sm !text-default-950 font-semibold'>
        Selected ({categoryFields.length + repFields.length})
      </p>

      <div className='flex flex-wrap gap-2 mt-2'>
        {categoryFields.map((value, index) => (
          <Chip
            key={value.id}
            size='sm'
            color='secondary'
            variant='flat'
            onClose={() => removeCategory(index)}
          >
            {value.description}
          </Chip>
        ))}
        {repFields.map((value, index) => (
          <Chip
            key={value.id}
            size='sm'
            color='primary'
            variant='flat'
            onClose={() => removeRep(index)}
          >
            {value.representativeFirstName} {value.representativeLastName}
          </Chip>
        ))}
      </div>

      <div className='flex flex-col mt-6'>
        <Input
          {...register('filter')}
          placeholder='Search categories or representatives to add'
          variant='bordered'
          size='sm'
          isClearable
          onClear={() => register('filter').onChange({ target: { value: '' } })}
        />
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mt-4'>
          <div className='space-y-2'>
            <div className='flex items-center gap-2'>
              <p className='text-sm font-medium text-default-800'>Categories</p>
              <p className='text-xs text-default-400'>({availableCategories.length} available)</p>
            </div>

            <div className='border border-default-200 rounded-lg p-3 max-h-64 overflow-y-auto space-y-2'>
              {showCategoriesEmptyState ? (
                <p className='text-xs text-default-400 text-center py-8'>No categories available</p>
              ) : hasAvailableCategories ? (
                availableCategories.map(category => (
                  <Button
                    key={category.id}
                    color='secondary'
                    variant='light'
                    size='sm'
                    className='w-full justify-start gap-2'
                    onPress={() => appendCategory(category)}
                  >
                    <PlusIcon className='size-4' />
                    {category.description}
                  </Button>
                ))
              ) : (
                <p className='text-xs text-default-400 text-center py-8'>
                  {filterWatch
                    ? 'No categories match your search term'
                    : 'All categories have been selected'}
                </p>
              )}
            </div>
          </div>

          {/* Individual Reps Column */}
          <div className='space-y-2'>
            <div className='flex items-center gap-2'>
              <p className='text-sm font-medium text-default-800'>Individual Representatives</p>
              <p className='text-xs text-default-400'>({availableReps.length} available)</p>
            </div>

            <div className='border border-default-200 rounded-lg p-3 max-h-64 overflow-y-auto space-y-2'>
              {isLoading ? (
                <div className='flex justify-center py-8'>
                  <Spinner size='sm' />
                </div>
              ) : showRepsEmptyState ? (
                <p className='text-xs text-default-400 text-center py-8'>
                  No representatives available
                </p>
              ) : hasAvailableReps ? (
                availableReps.map(rep => (
                  <Button
                    key={rep.id}
                    color='primary'
                    variant='light'
                    size='sm'
                    className='w-full justify-start gap-2'
                    onPress={() => appendRep(rep)}
                  >
                    <PlusIcon className='size-4' />
                    <span className='flex-1 text-left'>
                      {rep.representativeFirstName} {rep.representativeLastName}
                    </span>
                    {rep.isFavorite && <HeartIcon className='size-4' color='red' />}
                  </Button>
                ))
              ) : (
                <p className='text-xs text-default-400 text-center py-8'>
                  {filterWatch
                    ? 'No representatives match your search term'
                    : 'All representatives have been selected or are included in selected categories'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RepresentativeAccess;
